import { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Button,
  Container,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  Input,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { getRfiDocumentsInfo } from 'api/internaltools';
import { getKycDetails, getRfiTemplates } from 'api/kyc';
import { getRfiThread } from 'modules/kyc/utils/utils';
import { showError } from 'utils/notifications';
import KycRfiDetails from 'components/KycRfi/KycRfiDetails';
import KycRfiForm from 'components/KycRfi/KycRfiForm';
import KycRfiPreview from 'components/KycRfi/KycRfiPreview';
import LoadingErrorWrapper from 'components/LoadingErrorWrapper/LoadingErrorWrapper';

export default function RfiEntityDetail() {
  const [entityId, setEntityId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [kycDetails, setKycDetails] = useState(null);
  const [error, setError] = useState(null);
  const [documentInfoLoading, setDocumentInfoLoading] = useState(false);
  const [documentInfoError, setDocumentInfoError] = useState(null);
  const [documentInfo, setDocumentInfo] = useState([]);
  const [rfiTemplates, setRfiTemplates] = useState([]);
  const [rfiTagTypes, setRfiTagTypes] = useState([]);
  const [rfiTagSubtypes, setRfiTagSubtypes] = useState([]);
  const toast = useToast();

  const location = useLocation();
  const history = useHistory();

  const initialEntityId = location.state?.entityId || '';

  const fetchKycDetails = useCallback(async (id) => {
    if (!id) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await getKycDetails(id);
      const responseData = response?.data?.data;

      if (responseData) {
        setKycDetails({
          ...responseData,
          rfi_requested: responseData?.rfi_requested?.map((item) => ({
            ...JSON.parse(item?.value),
            created_at: item?.created_at,
            signed_rfi_document_urls: item?.signed_rfi_document_urls,
            id: item?.id,
            parent_rfi_id: item?.parent_rfi_id,
            rfi_thread: getRfiThread(responseData, item, []),
          })),
          rfi_submitted: responseData?.rfi_submitted?.map((item) => ({
            ...JSON.parse(item?.value),
            created_at: item?.created_at,
            signed_rfi_document_urls: item?.signed_rfi_document_urls,
            id: item?.id,
            parent_rfi_id: item?.parent_rfi_id,
            rfi_thread: getRfiThread(responseData, item, []),
          })),
        });
      } else {
        setKycDetails(null);
      }
    } catch (error) {
      setError(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getRfiDocumentsDetails = async () => {
    setDocumentInfoLoading(true);
    try {
      const response = await getRfiDocumentsInfo();

      setDocumentInfo(response?.data?.data);
    } catch (error) {
      showError(toast, 'Error fetching document info', error);
      setDocumentInfoError(error);
    } finally {
      setDocumentInfoLoading(false);
    }
  };

  const updateRfiTagsTypeOptions = (rfiTypes) => {
    const rfiTagTypesOptions = [];

    Object.entries(rfiTypes?.type).forEach(([key, value]) => {
      rfiTagTypesOptions.push({
        label: value,
        value: key,
      });
    });

    setRfiTagTypes(rfiTagTypesOptions);
  };

  const updateRfiTagsSubtypeOptions = (rfiTypes) => {
    const rfiTagSubtypesOptions = {};

    Object.entries(rfiTypes?.sub_type).forEach(([key, value]) => {
      rfiTagSubtypesOptions[key] = Object.entries(value).map(([subtypeKey, subtypeValue]) => ({
        label: subtypeValue,
        value: subtypeKey,
      }));
    });

    setRfiTagSubtypes(rfiTagSubtypesOptions);
  };

  const updateRfiTemplatesOptions = (templates) => {
    const templateOptions = [];

    Object.entries(templates).forEach(([key, value]) => {
      templateOptions.push({
        label: key,
        value: value,
      });
    });
    setRfiTemplates(templateOptions);
  };

  const fetchRfiTemplates = async () => {
    try {
      const response = await getRfiTemplates();
      const templates = response?.data?.data?.templates;
      const rfiTypes = response?.data?.data?.rfi_types;

      updateRfiTemplatesOptions(templates);
      updateRfiTagsTypeOptions(rfiTypes);
      updateRfiTagsSubtypeOptions(rfiTypes);
    } catch (error) {
      showError(toast, 'Error fetching RFI templates', error);
    }
  };

  useEffect(() => {
    if (initialEntityId) {
      setEntityId(initialEntityId);
      getRfiDocumentsDetails();
      fetchKycDetails(initialEntityId);
      fetchRfiTemplates();
    }
  }, [initialEntityId, fetchKycDetails]);

  return (
    <Container maxW='100%' py={8}>
      <HStack mb={3} spacing={10} align='center'>
        <Button
          colorScheme='gray'
          onClick={() => {
            history.goBack();
          }}
          size='md'
        >
          Back
        </Button>
        <Heading as='h1' size='lg' flex='1' textAlign='center'>
          {kycDetails?.resource_type} RFIs
        </Heading>
      </HStack>
      <VStack spacing={4} align='stretch'>
        <Grid templateColumns='repeat(2, 1fr)' gap={3}>
          <GridItem>
            <FormControl>
              <FormLabel>Entity ID</FormLabel>
              <Input
                value={entityId}
                onChange={(e) => setEntityId(e.target.value)}
                placeholder='Enter Entity ID'
                isDisabled={isLoading}
                onKeyDown={(e) => e.key === 'Enter' && entityId && fetchKycDetails(entityId)}
              />
            </FormControl>
          </GridItem>
          <GridItem alignSelf='end'>
            <Button
              colorScheme='blue'
              onClick={() => fetchKycDetails(entityId)}
              isLoading={isLoading}
              isDisabled={!entityId || isLoading}
              w='fit-content'
            >
              Proceed
            </Button>
          </GridItem>
        </Grid>
      </VStack>
      <LoadingErrorWrapper isLoading={isLoading} error={error}>
        {kycDetails && (
          <>
            <KycRfiPreview
              sumsub_applicant_id={kycDetails?.sumsub_applicant_id}
              entity_name={kycDetails?.entity_name}
              sumsub_kyc_status={kycDetails?.sumsub_kyc_status}
              kyc_status={kycDetails?.kyc_status}
              status_remark={kycDetails?.status_remark}
              sumsub_profile_url={kycDetails?.sumsub_profile_url}
              super_merchant_name={kycDetails?.super_merchant_name}
              entityId={entityId}
              fetchKycDetails={() => fetchKycDetails(entityId)}
              resourceType={kycDetails?.resource_type}
              tags={kycDetails?.tags}
            />
            <KycRfiForm
              entityId={entityId}
              fetchKycDetails={() => fetchKycDetails(entityId)}
              kyc_status={kycDetails?.kyc_status}
              documentInfo={documentInfo}
              documentInfoError={documentInfoError}
              documentInfoLoading={documentInfoLoading}
              rfiTemplates={rfiTemplates}
              resourceType={kycDetails?.resource_type}
              rfiTagTypes={rfiTagTypes}
              rfiTagSubtypes={rfiTagSubtypes}
            />
            <KycRfiDetails
              kycDetails={kycDetails}
              documentInfo={documentInfo}
              entityId={entityId}
              parentId={kycDetails?.parent_id}
              fetchKycDetails={() => fetchKycDetails(entityId)}
              rfiTemplates={rfiTemplates}
              resourceType={kycDetails?.resource_type}
              rfiTagTypes={rfiTagTypes}
              rfiTagSubtypes={rfiTagSubtypes}
            />
          </>
        )}
      </LoadingErrorWrapper>
    </Container>
  );
}
